import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ClockIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PhotoIcon,
  ShoppingCartIcon,
  TagIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const attributeLabels = {
  color: "Цвет",
  size: "Размер",
  weight: "Вес",
  volume: "Объем",
  material: "Материал",
  flavor: "Вкус",
  breed: "Порода",
  age: "Возраст",
};

const normalizeArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);

      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }

      if (parsed && typeof parsed === "object") {
        return [parsed];
      }
    } catch (error) {
      return [trimmed];
    }

    return [trimmed];
  }

  return [];
};

const normalizeImages = (value) => {
  return normalizeArray(value)
    .map((item) => {
      if (!item) return "";

      if (typeof item === "string") {
        return item;
      }

      if (typeof item === "object") {
        return item.url || item.src || item.image || item.image_url || "";
      }

      return "";
    })
    .filter(Boolean);
};

const normalizeAttributes = (value) => {
  if (!value) return {};

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      return {};
    }
  }

  return {};
};

const normalizeVariant = (variant) => {
  return {
    ...variant,
    attributes: normalizeAttributes(variant.attributes),
    image_gallery: normalizeImages(
      variant.image_gallery || variant.images || variant.gallery,
    ),
  };
};

const getListingKind = (item, fallbackType = "") => {
  const type = String(
    fallbackType ||
      item?.type ||
      item?.listing_type ||
      item?.item_type ||
      item?.entity_type ||
      "",
  )
    .toLowerCase()
    .trim();

  if (type === "product" || type === "products") {
    return "product";
  }

  if (type === "service" || type === "services") {
    return "service";
  }

  const hasServiceFields =
    item?.service_id !== undefined ||
    item?.advance_booking_days !== undefined ||
    item?.assigned_employees !== undefined ||
    item?.available_days !== undefined ||
    item?.buffer_time_after !== undefined ||
    item?.buffer_time_before !== undefined ||
    item?.duration !== undefined;

  if (hasServiceFields) {
    return "service";
  }

  return "product";
};

const getListingId = (item, type) => {
  if (type === "service") {
    return item?.service_id || item?.id;
  }

  return item?.product_id || item?.id;
};

const normalizeListing = (item, forcedType = "") => {
  const type = getListingKind(item, forcedType);

  const images = normalizeImages(
    item?.images || item?.image_gallery || item?.media_gallery || item?.gallery,
  );

  const mainImage =
    item?.image ||
    item?.image_url ||
    item?.cover_image ||
    item?.thumbnail ||
    images[0] ||
    "";

  const variants = normalizeArray(item?.variants).map(normalizeVariant);

  return {
    ...item,
    id: getListingId(item, type),
    type,
    name: item?.name || item?.title || "",
    description: item?.description || "",
    price: Number(item?.price || item?.base_price || 0),
    image: mainImage,
    images,
    variants,
    category: item?.category_name || item?.category || item?.category_id || "",
    company_id: item?.company_id || item?.business_id || "",
    company_name:
      item?.company_name || item?.company || item?.business_name || "",
    company_city: item?.company_city || item?.city || "",
    company_country: item?.company_country || item?.country || "",
    location:
      item?.location || [item?.city, item?.country].filter(Boolean).join(", "),
    sku: item?.sku || "",
    stock: Number(item?.stock ?? item?.inventory ?? item?.quantity ?? 0),
    duration: item?.duration || item?.duration_minutes || "",
    available_days: normalizeArray(item?.available_days),
    assigned_employees: normalizeArray(item?.assigned_employees),
    original_price: item?.original_price || null,
  };
};

const ProductPublicPage = () => {
  const params = useParams();
  const { addItem } = useCart();

  const productId = params.productId;
  const serviceId = params.serviceId;

  const routeType = serviceId ? "service" : "product";
  const routeItemId = serviceId || productId;

  const [listing, setListing] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [selectedImage, setSelectedImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isService = listing?.type === "service";
  const isProduct = listing?.type === "product";
  const variants = listing?.variants || [];

  const selectedVariant = useMemo(() => {
    if (!variants.length || !selectedVariantId) return null;

    return (
      variants.find(
        (variant) => String(variant.id) === String(selectedVariantId),
      ) || null
    );
  }, [variants, selectedVariantId]);

  const gallery = useMemo(() => {
    if (!listing) return [];

    const images = [
      ...(selectedVariant?.image_gallery || []),
      ...(listing.images || []),
      listing.image,
    ].filter(Boolean);

    return [...new Set(images)];
  }, [listing, selectedVariant]);

  const activeImage = gallery.includes(selectedImage)
    ? selectedImage
    : gallery[0] || "";

  const attributeOptions = useMemo(() => {
    const options = {};

    variants.forEach((variant) => {
      Object.entries(variant.attributes || {}).forEach(([key, value]) => {
        if (!value) return;

        if (!options[key]) {
          options[key] = new Set();
        }

        options[key].add(String(value));
      });
    });

    return Object.entries(options).map(([name, values]) => ({
      name,
      label: attributeLabels[name] || name,
      values: Array.from(values),
    }));
  }, [variants]);

  const currentPrice = Number(selectedVariant?.price ?? listing?.price ?? 0);
  const currentStock = Number(selectedVariant?.stock ?? listing?.stock ?? 0);
  const currentSku = selectedVariant?.sku || listing?.sku || "";
  const originalPrice = Number(listing?.original_price || 0);

  const discountPercent =
    originalPrice > currentPrice
      ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
      : 0;

  const fetchProductDetails = async (id) => {
    const response = await fetch(`/api/v1/public/products/${id}`);
    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success || !data.product) {
      throw new Error(data.error || "Не удалось загрузить товар");
    }

    return normalizeListing(data.product, "product");
  };

  const fetchMarketplaceDetails = async (type, id) => {
    const response = await fetch("/api/v1/public/marketplace");
    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      throw new Error(data.error || "Не удалось загрузить подробности");
    }

    const listings = data.listings || [];

    const found =
      listings.find((item) => {
        const itemType = getListingKind(item);
        const itemId = getListingId(item, itemType);

        return itemType === type && String(itemId) === String(id);
      }) ||
      listings.find((item) => {
        const itemType = getListingKind(item);
        const itemId = getListingId(item, itemType);

        return String(itemId) === String(id);
      });

    if (!found) {
      throw new Error(
        type === "service" ? "Услуга не найдена" : "Товар не найден",
      );
    }

    return normalizeListing(found, type);
  };

  const fetchListing = useCallback(async () => {
    if (!routeItemId) {
      setListing(null);
      setError("ID не найден");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      let loadedListing = null;

      if (routeType === "product") {
        try {
          loadedListing = await fetchProductDetails(routeItemId);
        } catch (productError) {
          loadedListing = await fetchMarketplaceDetails("product", routeItemId);
        }
      } else {
        loadedListing = await fetchMarketplaceDetails("service", routeItemId);
      }

      const defaultVariant =
        loadedListing.variants?.find((variant) => variant.is_default) ||
        loadedListing.variants?.[0] ||
        null;

      setListing(loadedListing);

      if (defaultVariant) {
        setSelectedVariantId(defaultVariant.id);
        setSelectedAttributes(defaultVariant.attributes || {});
        setSelectedImage(
          defaultVariant.image_gallery?.[0] ||
            loadedListing.images?.[0] ||
            loadedListing.image ||
            "",
        );
      } else {
        setSelectedVariantId(null);
        setSelectedAttributes({});
        setSelectedImage(
          loadedListing.images?.[0] || loadedListing.image || "",
        );
      }
    } catch (err) {
      setError(err.message || "Не удалось загрузить подробности");
      setListing(null);
    } finally {
      setLoading(false);
    }
  }, [routeItemId, routeType]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(price || 0));
  };

  const isAttributeValueAvailable = (attributeName, value) => {
    if (!variants.length) return true;

    return variants.some((variant) => {
      const attrs = variant.attributes || {};

      if (String(attrs[attributeName]) !== String(value)) {
        return false;
      }

      return Object.entries(selectedAttributes).every(
        ([key, selectedValue]) => {
          if (key === attributeName) return true;
          if (!selectedValue) return true;

          return String(attrs[key]) === String(selectedValue);
        },
      );
    });
  };

  const handleAttributeChange = (attributeName, value) => {
    const nextAttributes = {
      ...selectedAttributes,
      [attributeName]: value,
    };

    setSelectedAttributes(nextAttributes);

    const matchingVariant = variants.find((variant) => {
      const attrs = variant.attributes || {};

      return Object.entries(nextAttributes).every(([key, selectedValue]) => {
        if (!selectedValue) return true;

        return String(attrs[key]) === String(selectedValue);
      });
    });

    if (matchingVariant) {
      setSelectedVariantId(matchingVariant.id);
      setSelectedImage(
        matchingVariant.image_gallery?.[0] ||
          listing.images?.[0] ||
          listing.image ||
          "",
      );
    }
  };

  const handleAddToCart = () => {
    if (!listing) return;

    if (isProduct && currentStock <= 0) {
      return;
    }

    addItem({
      id:
        isProduct && selectedVariant
          ? `${listing.id}:${selectedVariant.id}`
          : listing.id,
      product_id: isProduct ? listing.id : null,
      service_id: isService ? listing.id : null,
      variant_id: selectedVariant?.id || null,
      type: listing.type,
      name: listing.name,
      company: listing.company_name,
      price: currentPrice,
      description: listing.description,
      image: activeImage || listing.image,
      sku: currentSku,
      duration: listing.duration,
      location: listing.location,
      variant: selectedVariant
        ? {
            id: selectedVariant.id,
            name: selectedVariant.variant_name,
            attributes: selectedVariant.attributes,
          }
        : null,
    });
  };

  const getVariantTitle = (variant) => {
    if (!variant) return "";

    if (variant.variant_name) {
      return variant.variant_name;
    }

    const attrs = Object.values(variant.attributes || {}).filter(Boolean);

    return attrs.length ? attrs.join(" / ") : "Вариант товара";
  };

  const actionDisabled = isProduct && currentStock <= 0;

  const actionText = isService
    ? "Добавить услугу"
    : currentStock > 0
      ? "Добавить в корзину"
      : "Нет в наличии";

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-lg font-bold text-zinc-500">
            Загрузка подробностей
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="w-full max-w-lg bg-white rounded-[40px] border border-zinc-100 p-10 text-center shadow-sm">
          <ExclamationTriangleIcon className="w-16 h-16 text-orange-500 mx-auto mb-4" />

          <h1 className="text-2xl font-black text-zinc-900 mb-3">
            Не удалось открыть подробности
          </h1>

          <p className="text-zinc-500 font-medium mb-8">{error}</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={fetchListing}
              className="px-6 py-3 rounded-2xl bg-zinc-900 text-white text-sm font-black hover:bg-zinc-800 transition-all"
            >
              Повторить
            </button>

            <Link
              to="/marketplace"
              className="px-6 py-3 rounded-2xl bg-zinc-100 text-zinc-700 text-sm font-black hover:bg-zinc-200 transition-all"
            >
              В маркетплейс
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="w-full max-w-lg bg-white rounded-[40px] border border-zinc-100 p-10 text-center shadow-sm">
          <CubeIcon className="w-16 h-16 text-zinc-300 mx-auto mb-4" />

          <h1 className="text-2xl font-black text-zinc-900 mb-3">
            Ничего не найдено
          </h1>

          <Link
            to="/marketplace"
            className="inline-flex items-center justify-center gap-2 text-orange-600 font-black hover:text-orange-700 transition-all"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Вернуться в маркетплейс
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20 pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/marketplace"
          className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-900 transition-all mb-8"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Назад в маркетплейс
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-10">
          {/* Gallery */}
          <div className="space-y-4">
            <div className="bg-white rounded-[40px] border border-zinc-100 p-4 shadow-sm">
              <div className="relative h-[420px] sm:h-[560px] rounded-[32px] overflow-hidden bg-zinc-100">
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={listing.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-zinc-300">
                    <PhotoIcon className="w-20 h-20 mb-4" />
                    <span className="text-sm font-black uppercase tracking-widest">
                      Нет изображения
                    </span>
                  </div>
                )}

                {discountPercent > 0 && (
                  <div className="absolute top-5 left-5 rounded-2xl bg-orange-500 px-4 py-2 text-xs font-black text-white shadow-lg">
                    -{discountPercent}%
                  </div>
                )}

                <div className="absolute top-5 right-5 rounded-2xl bg-white/90 px-4 py-2 text-xs font-black text-zinc-900 shadow-lg">
                  {isService
                    ? "Услуга"
                    : currentStock > 0
                      ? "В наличии"
                      : "Нет в наличии"}
                </div>
              </div>

              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-3 mt-4">
                  {gallery.slice(0, 10).map((image) => (
                    <button
                      key={image}
                      type="button"
                      onClick={() => setSelectedImage(image)}
                      className={`h-20 sm:h-24 rounded-2xl overflow-hidden border-2 transition-all ${
                        activeImage === image
                          ? "border-zinc-900"
                          : "border-zinc-100 hover:border-zinc-300"
                      }`}
                    >
                      <img
                        src={image}
                        alt={listing.name}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-[32px] border border-zinc-100 p-8 shadow-sm">
              <h2 className="text-2xl font-black text-zinc-900 mb-4">
                Описание
              </h2>

              <div className="text-zinc-500 font-medium leading-relaxed whitespace-pre-line">
                {listing.description || "Описание не заполнено."}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-5 lg:sticky lg:top-8 self-start">
            <div className="bg-white rounded-[40px] border border-zinc-100 p-8 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 mb-5">
                {listing.category && (
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-orange-50 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-orange-600">
                    <TagIcon className="w-4 h-4" />
                    {listing.category}
                  </div>
                )}

                <div className="inline-flex items-center gap-2 rounded-2xl bg-zinc-100 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-zinc-500">
                  <CubeIcon className="w-4 h-4" />
                  {isService ? "Услуга" : "Товар"}
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl font-black text-zinc-900 tracking-tight mb-5">
                {listing.name}
              </h1>

              {listing.company_name && (
                <Link
                  to={`/companies/${listing.company_id}`}
                  className="inline-flex items-center gap-3 rounded-2xl bg-zinc-50 px-4 py-3 text-sm font-bold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-all mb-8"
                >
                  <BuildingStorefrontIcon className="w-5 h-5 text-orange-500" />

                  <span>{listing.company_name}</span>

                  {(listing.company_city || listing.company_country) && (
                    <span className="text-zinc-400">
                      {listing.company_city}
                      {listing.company_city && listing.company_country
                        ? ", "
                        : ""}
                      {listing.company_country}
                    </span>
                  )}
                </Link>
              )}

              <div className="flex flex-wrap items-end gap-4 mb-8">
                <span className="text-4xl font-black text-zinc-900">
                  {formatPrice(currentPrice)}
                </span>

                {originalPrice > currentPrice && (
                  <span className="text-xl font-black text-zinc-300 line-through mb-1">
                    {formatPrice(originalPrice)}
                  </span>
                )}
              </div>

              {isProduct && attributeOptions.length > 0 && (
                <div className="space-y-5 mb-8">
                  <div>
                    <h2 className="text-lg font-black text-zinc-900">
                      Выберите вариант
                    </h2>

                    <p className="text-sm font-medium text-zinc-400 mt-1">
                      Цена и остаток обновятся автоматически
                    </p>
                  </div>

                  {attributeOptions.map((attribute) => (
                    <div key={attribute.name} className="space-y-3">
                      <div className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
                        {attribute.label}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {attribute.values.map((value) => {
                          const isActive =
                            String(selectedAttributes[attribute.name]) ===
                            String(value);

                          const isAvailable = isAttributeValueAvailable(
                            attribute.name,
                            value,
                          );

                          return (
                            <button
                              key={value}
                              type="button"
                              disabled={!isAvailable}
                              onClick={() =>
                                handleAttributeChange(attribute.name, value)
                              }
                              className={`px-5 py-3 rounded-2xl text-sm font-black border transition-all ${
                                isActive
                                  ? "bg-zinc-900 text-white border-zinc-900 shadow-lg"
                                  : isAvailable
                                    ? "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-900"
                                    : "bg-zinc-50 text-zinc-300 border-zinc-100 cursor-not-allowed"
                              }`}
                            >
                              {attribute.name === "size"
                                ? String(value).toUpperCase()
                                : value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isProduct && selectedVariant && (
                <div className="rounded-[28px] bg-zinc-50 border border-zinc-100 p-5 mb-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                        Выбранный вариант
                      </div>

                      <div className="text-base font-black text-zinc-900">
                        {getVariantTitle(selectedVariant)}
                      </div>
                    </div>

                    {currentSku && (
                      <div className="text-right">
                        <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                          SKU
                        </div>

                        <div className="text-sm font-black text-zinc-700">
                          {currentSku}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleAddToCart}
                disabled={actionDisabled}
                className="w-full inline-flex items-center justify-center gap-3 rounded-2xl bg-zinc-900 px-6 py-5 text-sm font-black uppercase tracking-widest text-white hover:bg-zinc-800 disabled:bg-zinc-200 disabled:text-zinc-400 disabled:cursor-not-allowed transition-all"
              >
                <ShoppingCartIcon className="w-5 h-5" />
                {actionText}
              </button>
            </div>

            <div className="bg-white rounded-[32px] border border-zinc-100 p-6 shadow-sm">
              {isService ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-zinc-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                      Длительность
                    </div>

                    <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
                      <ClockIcon className="w-5 h-5 text-orange-500" />
                      {listing.duration
                        ? `${listing.duration} мин`
                        : "Не указана"}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-zinc-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                      Локация
                    </div>

                    <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
                      <MapPinIcon className="w-5 h-5 text-orange-500" />
                      {listing.location || "Не указана"}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-zinc-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                      Тип
                    </div>

                    <div className="text-sm font-black text-zinc-900">
                      Услуга
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-zinc-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                      Остаток
                    </div>

                    <div
                      className={`flex items-center gap-2 text-sm font-black ${
                        currentStock > 0
                          ? "text-emerald-600"
                          : "text-orange-600"
                      }`}
                    >
                      {currentStock > 0 ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}

                      {currentStock > 0 ? `${currentStock} шт.` : "0 шт."}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-zinc-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                      Артикул
                    </div>

                    <div className="text-sm font-black text-zinc-900">
                      {currentSku || "Не указан"}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-zinc-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                      Тип
                    </div>

                    <div className="text-sm font-black text-zinc-900">
                      Товар
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isService && listing.available_days.length > 0 && (
              <div className="bg-white rounded-[32px] border border-zinc-100 p-6 shadow-sm">
                <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3">
                  Доступные дни
                </div>

                <div className="flex flex-wrap gap-2">
                  {listing.available_days.map((day) => (
                    <span
                      key={day}
                      className="rounded-2xl bg-zinc-100 px-4 py-2 text-xs font-black text-zinc-700"
                    >
                      {day}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedVariant?.wholesale_price && (
              <div className="bg-orange-50 rounded-[32px] border border-orange-100 p-6">
                <div className="text-[10px] font-black uppercase tracking-widest text-orange-500 mb-2">
                  Оптовая цена
                </div>

                <div className="text-2xl font-black text-orange-700">
                  {formatPrice(selectedVariant.wholesale_price)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPublicPage;
